import { Injectable, signal, computed, inject, NgZone } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Observable, throwError } from 'rxjs';
import { tap, catchError, map } from 'rxjs/operators';
import { environment } from '@env/environment';
import { AuthPort, MyOrganization } from '@core/ports/auth.port';
import { User, UpdateProfileRequest } from '@domains/auth/models/user.model';
import { LoginRequest, RegisterRequest, ValidateInvitationResponse } from '@domains/auth/models/auth-request.model';
import { ApiResponse, LoginResponseData, RegisterResponseData, RefreshResponseData, AvatarUploadResponseData } from '@domains/auth/models/auth-response.model';
import { TokenService } from './token.service';
import { FeatureService } from '@core/services/feature.service';
import { BrandingService } from '@core/services/branding.service';
import { CurrencyService } from '@core/services/currency.service';
import { DemoDataService } from '@core/services/demo-data.service';
import { NavigationService } from '@core/services/navigation.service';
import { NotificationService } from '@core/services/notification.service';
import { SKIP_AUTH_LOGOUT } from '@core/interceptors/auth.interceptor';

@Injectable({
  providedIn: 'root',
})
export class AuthService implements AuthPort {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly tokenService = inject(TokenService);
  private readonly featureService = inject(FeatureService);
  private readonly brandingService = inject(BrandingService);
  private readonly currencyService = inject(CurrencyService);
  private readonly demoDataService = inject(DemoDataService);
  private readonly navigationService = inject(NavigationService);
  private readonly notificationService = inject(NotificationService);
  private readonly ngZone = inject(NgZone);

  private readonly apiUrl = `${environment.authAPI}/auth`;

  private readonly currentUserSignal = signal<User | null>(null);
  private readonly isAuthenticatedSignal = signal<boolean>(false);
  private readonly isLoadingSignal = signal<boolean>(false);
  private readonly myOrganizationsSignal = signal<MyOrganization[]>([]);
  readonly status = signal<'loading' | 'loaded' | 'error' | 'idle'>('idle');

  readonly currentUser = this.currentUserSignal.asReadonly();
  readonly isAuthenticated = this.isAuthenticatedSignal.asReadonly();
  readonly isLoading = this.isLoadingSignal.asReadonly();
  readonly myOrganizations = this.myOrganizationsSignal.asReadonly();
  readonly userName = computed(() => {
    const user = this.currentUserSignal();
    return user ? `${user.firstName} ${user.lastName}` : '';
  });

  constructor() {
    this.initializeAuth();
    this.listenForCrossTabChanges();
  }

  private initializeAuth(): void {
    const token = this.tokenService.getAccessToken();
    if (!token) return;

    // Demo mode is session-only — never carry over from another org or session
    this.demoDataService.deactivate();

    if (this.tokenService.isTokenExpired(token)) {
      this.tryRefreshToken();
      return;
    }

    this.status.set('loading');
    this.isAuthenticatedSignal.set(true);

    // Restore user from localStorage while fetching fresh data
    const savedUser = this.tokenService.getSavedUser() as User | null;
    if (savedUser) {
      this.currentUserSignal.set(savedUser);
      this.featureService.setSuperAdmin((savedUser as User).role === 'super_admin');
    }

    this.fetchCurrentUser().subscribe({
      next: (user) => {
        this.currentUserSignal.set(user);
        this.tokenService.saveUser(user as unknown as Record<string, unknown>);
        this.status.set('loaded');
        // Load org features after restoring session
        this.featureService.setSuperAdmin(user.role === 'super_admin');
        this.featureService.loadFeatures().subscribe();
        this.listMyOrganizations().subscribe();
        this.brandingService.loadBranding();
        this.currencyService.loadCurrency();
      },
      error: (err) => {
        if (err?.status === 403) {
          this.notificationService.error(
            'Tu organización está inactiva. Consultá al administrador.',
            { duration: 8000 }
          );
          this.clearSession();
          this.status.set('idle');
          this.router.navigate(['/auth/login']);
          return;
        }
        if (savedUser) {
          this.status.set('loaded');
          this.featureService.loadFeatures().subscribe();
          this.listMyOrganizations().subscribe();
          this.brandingService.loadBranding();
          this.currencyService.loadCurrency();
        } else {
          this.clearSession();
          this.status.set('idle');
        }
      },
    });
  }

  login(email: string, password: string): Observable<User> {
    this.isLoadingSignal.set(true);

    const payload: LoginRequest = { email, password };

    return this.http.post<ApiResponse<LoginResponseData>>(`${this.apiUrl}/login`, payload).pipe(
      map((response) => {
        const { user, tokens } = response.data;
        this.tokenService.setAccessToken(tokens.accessToken);
        this.tokenService.setRefreshToken(tokens.refreshToken);
        this.tokenService.saveUser(user as unknown as Record<string, unknown>);
        this.currentUserSignal.set(user);
        this.isAuthenticatedSignal.set(true);
        this.isLoadingSignal.set(false);
        this.status.set('loaded');
        // Load org features and orgs after login
        this.featureService.setSuperAdmin(user.role === 'super_admin');
        this.featureService.loadFeatures().subscribe();
        this.listMyOrganizations().subscribe();
        this.brandingService.loadBranding();
        this.currencyService.loadCurrency();
        return user;
      }),
      catchError((error) => {
        this.isLoadingSignal.set(false);
        return throwError(() => error);
      })
    );
  }

  register(data: Record<string, unknown>): Observable<unknown> {
    return this.http.post<ApiResponse<RegisterResponseData>>(
      `${this.apiUrl}/register`,
      data as unknown as RegisterRequest
    );
  }

  requestPasswordReset(data: { email: string }): Observable<unknown> {
    return this.http.post<ApiResponse>(`${this.apiUrl}/password-reset/request`, data);
  }

  confirmPasswordReset(data: { token: string; newPassword: string }): Observable<unknown> {
    return this.http.post<ApiResponse>(`${this.apiUrl}/password-reset/confirm`, data);
  }

  verifyEmail(data: { token: string }): Observable<unknown> {
    return this.http.post<ApiResponse>(`${this.apiUrl}/verify-email`, data);
  }

  resendVerificationEmail(email: string): Observable<unknown> {
    return this.http.post<ApiResponse>(`${this.apiUrl}/resend-verification`, { email });
  }

  validateInvitation(token: string): Observable<ValidateInvitationResponse> {
    return this.http.get<ApiResponse<ValidateInvitationResponse>>(
      `${this.apiUrl}/invitations/${token}`
    ).pipe(map((response) => response.data));
  }

  uploadAvatar(file: File): Observable<AvatarUploadResponseData> {
    const formData = new FormData();
    formData.append('avatar', file);

    return this.http.post<ApiResponse<AvatarUploadResponseData>>(
      `${environment.authAPI}/upload/avatar`,
      formData
    ).pipe(
      map((response) => {
        this.updateUserAvatar(response.data.avatarUrl);
        return response.data;
      })
    );
  }

  deleteAvatar(): Observable<unknown> {
    return this.http.delete<ApiResponse>(`${environment.authAPI}/upload/avatar`).pipe(
      tap(() => this.updateUserAvatar(undefined))
    );
  }

  changePassword(currentPassword: string, newPassword: string): Observable<void> {
    return this.http.put<ApiResponse>(`${this.apiUrl}/change-password`, { currentPassword, newPassword }).pipe(
      map(() => undefined)
    );
  }

  updateProfile(data: Record<string, unknown>): Observable<User> {
    return this.http.put<ApiResponse<User>>(`${this.apiUrl}/me`, data as unknown as UpdateProfileRequest).pipe(
      map((response) => {
        const user = response.data;
        this.currentUserSignal.set(user);
        this.tokenService.saveUser(user as unknown as Record<string, unknown>);
        return user;
      })
    );
  }

  private updateUserAvatar(avatarUrl: string | undefined): void {
    const current = this.currentUserSignal();
    if (!current) return;
    const updated = { ...current, avatar: avatarUrl };
    this.currentUserSignal.set(updated);
    this.tokenService.saveUser(updated as unknown as Record<string, unknown>);
  }

  listMyOrganizations(): Observable<MyOrganization[]> {
    return this.http.get<ApiResponse<MyOrganization[]>>(`${this.apiUrl}/me/organizations`).pipe(
      map((response) => {
        const orgs = response.data ?? [];
        this.myOrganizationsSignal.set(orgs);
        return orgs;
      })
    );
  }

  switchOrg(organizationId: string): Observable<unknown> {
    return this.http.post<ApiResponse<LoginResponseData>>(`${this.apiUrl}/switch-org`, { organizationId }, {
      context: new HttpContext().set(SKIP_AUTH_LOGOUT, true),
    }).pipe(
      map((response) => {
        const { user, tokens } = response.data;
        this.tokenService.setAccessToken(tokens.accessToken);
        this.tokenService.setRefreshToken(tokens.refreshToken);
        this.tokenService.saveUser(user as unknown as Record<string, unknown>);
        this.currentUserSignal.set(user);
        this.featureService.setSuperAdmin(user.role === 'super_admin');
        this.featureService.loadFeatures().subscribe();
        this.brandingService.loadBranding();
        this.currencyService.loadCurrency();
        this.demoDataService.deactivate();
        return response.data;
      })
    );
  }

  refreshAccessToken(): Observable<string> {
    const refreshToken = this.tokenService.getRefreshToken();
    if (!refreshToken) {
      return throwError(() => new Error('No refresh token'));
    }

    return this.http.post<ApiResponse<RefreshResponseData>>(`${this.apiUrl}/refresh`, { refreshToken }).pipe(
      map((response) => {
        const { accessToken } = response.data;
        this.tokenService.setAccessToken(accessToken);
        return accessToken;
      }),
      catchError((error) => {
        this.clearSession();
        this.router.navigate(['/auth/login']);
        return throwError(() => error);
      })
    );
  }

  logout(): void {
    const refreshToken = this.tokenService.getRefreshToken();
    if (refreshToken) {
      this.http.post(`${this.apiUrl}/logout`, { refreshToken }).subscribe();
    }
    this.dialog.closeAll();
    this.clearSession();
    this.router.navigate(['/auth/login']);
  }

  /**
   * Clears the local session (tokens + signals) without API call or navigation.
   * Used after registration so a newly registered user can log in fresh
   * even if another user was already authenticated in the same browser.
   */
  silentLogout(): void {
    this.clearSession();
  }

  /**
   * Verifica si el usuario tiene un permiso específico.
   */
  hasPermission(permission: string): boolean {
    return this.currentUserSignal()?.permissions?.includes(permission) ?? false;
  }

  /**
   * Verifica si el usuario tiene al menos UNO de los permisos.
   */
  containsAny(permissions: string[]): boolean {
    const userPermissions = this.currentUserSignal()?.permissions;
    if (!userPermissions) return false;
    return permissions.some((p) => userPermissions.includes(p));
  }

  /**
   * Verifica si el usuario tiene TODOS los permisos.
   */
  containsAll(permissions: string[]): boolean {
    const userPermissions = this.currentUserSignal()?.permissions;
    if (!userPermissions) return false;
    return permissions.every((p) => userPermissions.includes(p));
  }

  hasRole(role: string): boolean {
    return this.currentUserSignal()?.role === role;
  }

  private fetchCurrentUser(): Observable<User> {
    return this.http.get<ApiResponse<User>>(`${this.apiUrl}/me`, {
      context: new HttpContext().set(SKIP_AUTH_LOGOUT, true),
    }).pipe(
      map((response) => response.data)
    );
  }

  private tryRefreshToken(): void {
    const refreshToken = this.tokenService.getRefreshToken();
    if (!refreshToken) {
      this.clearSession();
      return;
    }

    this.status.set('loading');
    this.refreshAccessToken().subscribe({
      next: () => this.initializeAuth(),
      error: () => {
        this.clearSession();
        this.status.set('idle');
      },
    });
  }

  /**
   * Listens for localStorage changes from other tabs to sync auth state.
   * The `storage` event only fires for changes made in OTHER tabs/windows.
   */
  private listenForCrossTabChanges(): void {
    window.addEventListener('storage', (event: StorageEvent) => {
      if (event.key !== 'sc_access_token') return;

      this.ngZone.run(() => {
        if (event.newValue) {
          this.initializeAuth();
        } else {
          this.clearSession();
          this.status.set('idle');
          this.router.navigate(['/auth/login']);
        }
      });
    });
  }

  private clearSession(): void {
    this.tokenService.clearTokens();
    this.navigationService.clearLastRoute();
    this.currentUserSignal.set(null);
    this.isAuthenticatedSignal.set(false);
    this.myOrganizationsSignal.set([]);
    this.featureService.clear();
    this.currencyService.reset();
    this.demoDataService.deactivate();
  }
}
